import React, { useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { PlusCircle, X, ChevronLeft, ChevronRight, Calendar, AlertTriangle, Ban } from 'lucide-react';
import ReservationForm from '../components/ReservationForm';
import ReservationList from "../components/ReservationList"; // Your existing form component
import Sidebar from '../components/Sidebar'; // Import ajouté

const ReservationPage = () => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const handleSuccess = () => {
        Swal.fire({
            title: t('success'),
            text: t('success_reservation_created'),
            icon: 'success',
            confirmButtonText: 'OK',
            customClass: {
                confirmButton: 'bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors'
            }
        });
        setIsModalOpen(false);
        setRefreshKey(prev => prev + 1); // Refresh the list
    };

    const handleError = (errors) => {
        Swal.fire({
            title: t('error'),
            html: `<div class="${isRtl ? 'text-right' : 'text-left'}">${errors.map(err => `<p class="mb-2">❌ ${err}</p>`).join('')}</div>`,
            icon: 'error',
            confirmButtonText: t('understood'),
            customClass: {
                confirmButton: 'bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg transition-colors'
            }
        });
        // Don't close the modal on error
    };

    return (
        <Sidebar>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" dir={isRtl ? 'rtl' : 'ltr'}>
                <div className={`flex justify-between items-center mb-8 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <h1 className={`text-2xl font-bold text-gray-900 ${isRtl ? 'text-right' : 'text-left'}`}>{t('reservations_management')}</h1>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className={`flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors shadow-md ${isRtl ? 'flex-row-reverse' : ''}`}
                    >
                        <PlusCircle className="w-5 h-5" />
                        {t('new_reservation')}
                    </button>
                </div>

                {/* Reservation List */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <ReservationList key={refreshKey} />
                </div>

                {/* Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                            {/* Background overlay */}
                            <div
                                className="fixed inset-0 transition-opacity"
                                aria-hidden="true"
                                onClick={() => setIsModalOpen(false)}
                            >
                                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                            </div>

                            {/* Modal container */}
                            <div className={`inline-block align-bottom bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full ${isRtl ? 'text-right' : 'text-left'}`}>
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <div className={`flex justify-between items-start ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <div className={isRtl ? 'text-right' : 'text-left'}>
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                                {t('create_new_reservation')}
                                            </h3>
                                            <p className="mt-1 text-sm text-gray-500">
                                                {t('fill_reservation_details')}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setIsModalOpen(false)}
                                            className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                        >
                                            <X className="h-6 w-6" />
                                        </button>
                                    </div>

                                    <div className="mt-6">
                                        <ReservationForm
                                            onSuccess={handleSuccess}
                                            onError={handleError}
                                            onCancel={() => setIsModalOpen(false)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Sidebar>
    );
};

export default ReservationPage;
